/**
 * pipeline/prompt-pipeline.ts
 *
 * Inbound pipeline: ContentBlock[] → segment → upload (concurrent) → reference → MDC PUT.
 *
 * All non-text blocks are uploaded concurrently via /api/storage/upload-prepare → PUT →
 * /api/storage/upload-complete. The full prompt is serialised as a .mdc file and uploaded
 * to the messages bucket. Tags are created and assigned for each uploaded attachment.
 *
 * Zero knowledge of transport or queue — called by agent.ts per dequeued task.
 * All config injected at the entrypoint boundary.
 */

import type * as acp from "@agentclientprotocol/sdk";
import type { Reference } from "../../../../lib/proto/gen/typescript/agency/reference/v1/reference";
import { assertHttpOk } from "../http-errors";
import { buildPsrefUri, parseReferences, kindFromMime } from "@pineseed/spec/psref";
import { assembleMdc } from "./mdc";
import type { ReferenceKindSlug } from "@pineseed/spec/mdc";
import type {
  PromptTask,
  TextSegment,
  MediaSegment,
  MdcHeader,
  MdcUploadReference,
  MdcInlineReference,
  UploadBucket,
  MessagePipelineConfig,
  SessionScope,
} from "./types";
import { StorageUploader } from "@pineseed/storage-client";
import type { UploadSubScope } from "@pineseed/storage-client";

// ─── Config ───────────────────────────────────────────────────────────────────

export interface PromptPipelineConfig extends MessagePipelineConfig {
  sessionId: string;
  workspaceId: string;
  participantId: string;
  scope: SessionScope;
  /** Transport for all storage/tag requests; the caller decides routing. */
  fetchApi: typeof fetch;
  // uploadTtlS inherited from MessagePipelineConfig
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── PromptPipeline ───────────────────────────────────────────────────────────

export class PromptPipeline {
  readonly #config: PromptPipelineConfig;
  readonly #uploader: StorageUploader;
  readonly #tagsUrl: string;
  readonly #assignUrl: string;

  constructor(config: PromptPipelineConfig) {
    this.#config = config;
    this.#uploader = new StorageUploader({
      apiUrl: config.apiUrl,
      authHeaders: config.authHeaders,
      ...(config.fetchOptions && { fetchOptions: config.fetchOptions }),
      fetchApi: config.fetchApi,
      uploadTtlS: config.uploadTtlS,
    });
    this.#tagsUrl = `${config.apiUrl}/api/tags`;
    this.#assignUrl = `${config.apiUrl}/api/tags/assign`;
  }

  /**
   * Process one prompt task:
   *   1. Segment blocks by type
   *   2. Upload all media concurrently (prepare → PUT → complete)
   *   3. Assemble MDC (header + body with psref substitutions)
   *   4. Upload MDC to messages bucket (prepare → PUT → complete)
   * Returns the messageId for use in the prompt_ready notification.
   */
  async process(task: PromptTask): Promise<{ messageId: string }> {
    const { messageId, params } = task;
    const { sessionId } = this.#config;

    // 1. Segment
    const textSegments: TextSegment[] = [];
    const mediaSegments: MediaSegment[] = [];

    for (let i = 0; i < params.prompt.length; i++) {
      const block = params.prompt[i]!;
      switch (block.type) {
        case "text":
          textSegments.push({
            type: "text",
            position: i,
            block: block as acp.TextContent & { type: "text" },
          });
          break;
        case "image":
        case "audio":
        case "resource":
        case "resource_link":
          mediaSegments.push({
            type: "media",
            position: i,
            block: block as
              | (acp.ImageContent & { type: "image" })
              | (acp.AudioContent & { type: "audio" })
              | (acp.EmbeddedResource & { type: "resource" })
              | (acp.ResourceLink & { type: "resource_link" }),
          });
          break;
        default:
          throw new Error(
            `PromptPipeline: unknown block type: ${(block as any).type}`,
          );
      }
    }

    // 2. Upload all media concurrently
    await Promise.all(
      mediaSegments.map((seg, idx) =>
        this.#processMediaSegment(seg, messageId, idx),
      ),
    );

    // 3. Assemble MDC body
    const totalSlots = params.prompt.length;
    const textByPos = new Map<number, TextSegment>(
      textSegments.map((s) => [s.position, s]),
    );
    const mediaByPos = new Map<number, MediaSegment>(
      mediaSegments.map((s) => [s.position, s]),
    );

    const bodyParts: string[] = [];
    for (let i = 0; i < totalSlots; i++) {
      const text = textByPos.get(i);
      if (text) {
        bodyParts.push((text.block as acp.TextContent).text ?? "");
        continue;
      }
      const media = mediaByPos.get(i);
      if (media?.reference) {
        const ref = media.reference;
        const isImage = ref.type.startsWith("image/");
        const title = ref.title || ref.id;
        bodyParts.push(
          isImage
            ? `![@${title}](${ref.psrefUri})`
            : `[@${title}](${ref.psrefUri})`,
        );
      }
    }

    const body = bodyParts.join("\n\n");

    // 4. Assemble MDC header
    const uploadRefs: MdcUploadReference[] = mediaSegments
      .filter(
        (
          s,
        ): s is MediaSegment & {
          filename: string;
          timestamp: number;
          reference: Reference;
        } =>
          s.filename !== undefined &&
          s.timestamp !== undefined &&
          s.reference !== undefined,
      )
      .map((s) => ({
        source: "upload" as const,
        position: s.position,
        reference: s.reference,
        filename: s.filename,
        timestamp: s.timestamp,
        bucket: "attachments" as UploadBucket,
      }));

    const seenPsrefUris = new Set<string>();
    const inlineRefs: MdcInlineReference[] = [];
    for (const seg of textSegments) {
      const text = (seg.block as acp.TextContent).text ?? "";
      for (const pr of parseReferences(text, this.#config.mimeResolver)) {
        const uri = pr.reference.psrefUri;
        if (seenPsrefUris.has(uri)) continue;
        seenPsrefUris.add(uri);
        inlineRefs.push({
          source: "inline" as const,
          position: seg.position,
          reference: pr.reference,
          kindSlug: pr.kindSlug as ReferenceKindSlug,
        });
      }
    }

    const mdcRefs = [...uploadRefs, ...inlineRefs].sort(
      (a, b) => a.position - b.position,
    );

    const header: MdcHeader = {
      messageId,
      sessionId,
      references: mdcRefs,
      participant: { id: this.#config.participantId, role: "guest" },
    };
    const mdcContent = assembleMdc(header, body);

    // 5. Upload MDC to messages bucket
    const mdcBytes = new TextEncoder().encode(mdcContent);
    const mdcFilename = messageId;
    await this.#storageUpload(
      mdcBytes,
      "text/markdown",
      mdcFilename,
      "messages",
      {},
    );

    return { messageId };
  }

  // ─── Internal ───────────────────────────────────────────────────────────────

  async #processMediaSegment(
    seg: MediaSegment,
    messageId: string,
    attachmentIndex: number,
  ): Promise<void> {
    const block = seg.block;

    // resource_link: already stored — build reference directly, no upload
    if (block.type === "resource_link") {
      const rl = block as acp.ResourceLink & { type: "resource_link" };
      if (!rl.uri) throw new Error("PromptPipeline: resource_link missing uri");
      const pathname = new URL(rl.uri).pathname;
      const mimeType = rl.mimeType
        ? rl.mimeType
        : this.#config.mimeResolver(pathname.slice(pathname.lastIndexOf(".") + 1));
      const ext = this.#config.mimeToExtResolver(mimeType);
      const kind = kindFromMime(mimeType);
      const title = rl.name ?? rl.uri;
      const passthrough = this.#config.resourceLinkPassthrough?.(kind, mimeType, pathname) ?? false;
      const refUri = passthrough
        ? rl.uri
        : buildPsrefUri({ kind, id: rl.uri, ext, version: "", tenant: "", params: {} });
      await this.#tagUpload(rl.uri, Date.now(), mimeType, title, messageId);
      seg.filename = rl.uri;
      seg.reference = {
        kind,
        id: rl.uri,
        ext,
        type: mimeType,
        version: "",
        tenant: "",
        title,
        description: "",
        uri: refUri,
        psrefUri: refUri,
        params: {},
      };
      return;
    }

    // Extract base64 + mimeType from inline blocks
    let base64: string;
    let mimeType: string;
    let name: string;
    let title: string;

    switch (block.type) {
      case "image": {
        const ib = block as acp.ImageContent & { type: "image" };
        if (!ib.mimeType)
          throw new Error("PromptPipeline: image block missing mimeType");
        if (!ib.data)
          throw new Error("PromptPipeline: image block missing data");
        base64 = ib.data;
        mimeType = ib.mimeType;
        const ext = this.#config.mimeToExtResolver(mimeType);
        name = `${messageId}_${attachmentIndex}.${ext}`;
        title = name;
        break;
      }
      case "audio": {
        const ab = block as acp.AudioContent & { type: "audio" };
        if (!ab.mimeType)
          throw new Error("PromptPipeline: audio block missing mimeType");
        if (!ab.data)
          throw new Error("PromptPipeline: audio block missing data");
        base64 = ab.data;
        mimeType = ab.mimeType;
        const ext = this.#config.mimeToExtResolver(mimeType);
        name = `${messageId}_${attachmentIndex}.${ext}`;
        title = name;
        break;
      }
      case "resource": {
        const er = block as acp.EmbeddedResource & { type: "resource" };
        const res = (er as any).resource as any;
        if (!res)
          throw new Error(
            "PromptPipeline: resource block missing resource field",
          );
        const pathname = new URL(res.uri as string).pathname;
        if (res.mimeType) {
          mimeType = res.mimeType;
        } else {
          const uriExt = pathname.slice(pathname.lastIndexOf(".") + 1);
          mimeType = this.#config.mimeResolver(uriExt);
        }
        const ext = this.#config.mimeToExtResolver(mimeType);
        if ("text" in res && res.text) {
          base64 = Buffer.from(res.text as string, "utf-8").toString("base64");
        } else if ("blob" in res && res.blob) {
          base64 = res.blob as string;
        } else {
          throw new Error(
            "PromptPipeline: resource block has neither text nor blob",
          );
        }
        name = `${messageId}_${attachmentIndex}.${ext}`;
        title = pathname.slice(pathname.lastIndexOf("/") + 1);
        break;
      }
      default:
        throw new Error(
          `PromptPipeline: unhandled media block type: ${(block as any).type}`,
        );
    }

    const ext = this.#config.mimeToExtResolver(mimeType);
    const binary = Buffer.from(base64, "base64");

    const { filename, timestamp } = await this.#storageUpload(
      binary,
      mimeType,
      name,
      "attachments",
      {},
    );

    await this.#tagUpload(filename, timestamp, mimeType, title, messageId);

    const kind = kindFromMime(mimeType);
    const psrefUri = buildPsrefUri({
      kind,
      id: filename,
      ext,
      version: "",
      tenant: "",
      params: {},
    });

    seg.filename = filename;
    seg.timestamp = timestamp;
    seg.reference = {
      kind,
      id: filename,
      ext,
      type: mimeType,
      version: "",
      tenant: "",
      title,
      description: "",
      uri: psrefUri,
      psrefUri,
      params: {},
    };
  }

  /**
   * Prepare → PUT → complete for any content.
   * Delegates to shared StorageUploader.
   */
  async #storageUpload(
    data: Uint8Array | Buffer,
    contentType: string,
    filename: string,
    bucketKey: string,
    subScope: UploadSubScope,
  ): Promise<{ filename: string; timestamp: number }> {
    return this.#uploader.upload(data, contentType, filename, bucketKey, {
      conversation: this.#config.scope.conversationId,
      thread: this.#config.sessionId,
      ...subScope,
    });
  }

  async #tagUpload(
    filename: string,
    timestamp: number,
    mimeType: string,
    name: string,
    messageId: string,
  ): Promise<void> {
    const { sessionId, workspaceId, participantId, authHeaders, fetchOptions, fetchApi } =
      this.#config;

    const id = await sha256Hex(`${sessionId}:${filename}:${timestamp}`);
    const description = `${name} (${mimeType})`;
    const tagType =
      mimeType.startsWith("image/") ||
      mimeType.startsWith("audio/") ||
      mimeType.startsWith("video/")
        ? "media"
        : "uri";

    const createRes = await fetchApi(this.#tagsUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      ...fetchOptions,
      body: JSON.stringify({
        id,
        name,
        type: tagType,
        workspaceId,
        namespace: "agent",
        description,
        metadata: {
          filename,
          timestamp: String(timestamp),
          mimeType,
          sessionId,
          messageId,
        },
      }),
    });
    await assertHttpOk(createRes, this.#tagsUrl);

    const assignRes = await fetchApi(this.#assignUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      ...fetchOptions,
      body: JSON.stringify({
        tagId: id,
        entityType: "thread",
        entityId: sessionId,
        workspaceId,
        assignedBy: participantId,
      }),
    });
    await assertHttpOk(assignRes, this.#assignUrl);
  }
}
